import { useState } from 'react';
import { Upload, Sparkles, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { useCompleteOnboarding, useOnboardingStatus } from '@/hooks/useOnboarding';

interface WelcomeModalProps {
  isOpen: boolean;
  onComplete: (result: { projectId?: string }) => void;
}

type Step = 1 | 2 | 3;
type OnboardingRole = '' | 'project_manager' | 'engineering_manager' | 'director' | 'other';
type OnboardingTool = '' | 'ms_project' | 'smartsheet' | 'excel' | 'other';
type OnboardingTeamSize = '' | '1-5' | '5-20' | '20-100' | '100+';

const ROLES = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'engineering_manager', label: 'Engineering Manager' },
  { value: 'director', label: 'Director' },
  { value: 'other', label: 'Other' },
] as const;

const TOOLS = [
  { value: 'ms_project', label: 'MS Project' },
  { value: 'smartsheet', label: 'Smartsheet' },
  { value: 'excel', label: 'Excel / Spreadsheets' },
  { value: 'other', label: 'Other' },
] as const;

const TEAM_SIZES = ['1-5', '5-20', '20-100', '100+'] as const;

export function WelcomeModal({ isOpen, onComplete }: WelcomeModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [organizationName, setOrganizationName] = useState('');
  const [role, setRole] = useState<OnboardingRole>('');
  const [currentTool, setCurrentTool] = useState<OnboardingTool>('');
  const [teamSize, setTeamSize] = useState<OnboardingTeamSize>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: onboardingStatus } = useOnboardingStatus();
  const completeOnboarding = useCompleteOnboarding();

  // Pre-fill org name if available
  const orgNameValue = organizationName || onboardingStatus?.organizationName || '';

  const buildPayload = (createSampleProject: boolean) => ({
    organizationName: orgNameValue.trim() || undefined,
    role: role || undefined,
    currentTool: currentTool || undefined,
    teamSize: teamSize || undefined,
    createSampleProject,
  });

  if (!isOpen) return null;

  const handleComplete = async (createSampleProject: boolean) => {
    setSubmitError(null);
    try {
      const result = await completeOnboarding.mutateAsync(buildPayload(createSampleProject));
      onComplete({ projectId: result.projectId });
    } catch (error: any) {
      const message = error?.response?.data?.message;
      setSubmitError(Array.isArray(message) ? message.join(', ') : message || 'Failed to complete onboarding.');
    }
  };

  const handleImportChoice = async () => {
    setSubmitError(null);
    try {
      await completeOnboarding.mutateAsync(buildPayload(false));
      onComplete({});
    } catch (error: any) {
      const message = error?.response?.data?.message;
      setSubmitError(Array.isArray(message) ? message.join(', ') : message || 'Failed to complete onboarding.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-background/95 backdrop-blur-xl rounded-2xl shadow-2xl border overflow-hidden">
        {/* Progress bar */}
        <div className="flex items-center gap-1 px-6 pt-6">
          {([1, 2, 3] as const).map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div
                className={`h-1.5 w-full rounded-full transition-colors ${
                  s <= step ? 'bg-primary' : 'bg-muted'
                }`}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground px-6 mt-2">
          Step {step} of 3
        </p>

        {/* Step content */}
        <div className="px-6 py-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">
                  Welcome to Wandool
                </h2>
                <p className="text-muted-foreground mt-1">
                  Let's get your workspace set up in under a minute.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="org-name"
                    className="text-sm font-medium leading-none"
                  >
                    Organization name
                  </label>
                  <input
                    id="org-name"
                    type="text"
                    value={orgNameValue}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Acme Corp"
                    className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">
                    Your role
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as OnboardingRole)}
                    className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Select a role...</option>
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Continue
                  <ChevronRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">
                  Tell us about your workflow
                </h2>
                <p className="text-muted-foreground mt-1">
                  This helps us tailor your experience.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">
                    Current tool
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {TOOLS.map((tool) => (
                      <button
                        key={tool.value}
                        onClick={() => setCurrentTool(tool.value)}
                        className={`flex items-center gap-2 rounded-lg border p-3 text-sm text-left transition-colors hover:border-primary ${
                          currentTool === tool.value
                            ? 'ring-2 ring-primary border-primary bg-primary/5'
                            : 'border-border'
                        }`}
                      >
                        <span className="font-medium">{tool.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">
                    Team size
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {TEAM_SIZES.map((size) => (
                      <button
                        key={size}
                        onClick={() => setTeamSize(size)}
                        className={`rounded-lg border p-3 text-sm font-medium text-center transition-colors hover:border-primary ${
                          teamSize === size
                            ? 'ring-2 ring-primary border-primary bg-primary/5'
                            : 'border-border'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center justify-center rounded-lg border border-border px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Continue
                  <ChevronRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">
                  Ready to go!
                </h2>
                <p className="text-muted-foreground mt-1">
                  Choose how you'd like to get started.
                </p>
              </div>

              <div className="space-y-3">
                {/* Import option */}
                <button
                  onClick={handleImportChoice}
                  disabled={completeOnboarding.isPending}
                  className="group flex w-full items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <Upload className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      Import an existing project
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Bring your MS Project, CSV, or Excel schedule
                    </p>
                  </div>
                </button>

                {/* Sample project option */}
                <button
                  onClick={() => handleComplete(true)}
                  disabled={completeOnboarding.isPending}
                  className="group flex w-full items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                    {completeOnboarding.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <Sparkles className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      Start with a sample project
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Pre-populated engineering project with 20 tasks,
                      dependencies, and critical path
                    </p>
                  </div>
                </button>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center justify-center rounded-lg border border-border px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={() => handleComplete(false)}
                  disabled={completeOnboarding.isPending}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {submitError && (
            <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
